import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";

const tracks = [
  { id: "ai", label: "Build a Custom AI/Automation System" },
  { id: "app", label: "Design & Engineer a Web/Mobile App" },
  { id: "pm", label: "End-to-End Technical Project Management" },
  { id: "consult", label: "Enterprise Consultation" },
];

const timelines = [
  { id: "immediate", label: "Immediate Velocity (Critical Launch)" },
  { id: "30-60", label: "30–60 Day Strategic Rollout" },
  { id: "explore", label: "Exploratory Phase" },
];

export default function Terminal() {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const [selectedTracks, setSelectedTracks] = useState([]);
  const [timeline, setTimeline] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [company, setCompany] = useState("");
  const [brief, setBrief] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const toggleTrack = (id) =>
    setSelectedTracks((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !brief.trim() || !password) {
      toast.error("ERROR · name, email, password, and brief are required.");
      return;
    }
    if (password.length < 8) {
      toast.error("ERROR · password must be at least 8 characters.");
      return;
    }
    if (selectedTracks.length === 0) {
      toast.error("ERROR · select at least one track.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        email: email.trim(),
        password,
        name: name.trim(),
        company: company.trim() || null,
        tracks: selectedTracks,
        timeline: timeline || null,
        brief: brief.trim(),
      };
      await signup(payload);
      toast.success("OK · account provisioned · redirecting to portal…");
      setDone(true);
      setTimeout(() => navigate("/portal", { replace: true }), 1200);
    } catch (err) {
      const status = err?.response?.status;
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || "Submission failed. Try again.";
      if (status === 409) {
        // Duplicate email — fall back to submitting the intake only (no account)
        try {
          await api.post(`/projects/initialize`, {
            tracks: selectedTracks,
            timeline: timeline || null,
            name: name.trim(),
            email: email.trim(),
            company: company.trim() || null,
            brief: brief.trim(),
          });
          toast.info("Account exists · intake recorded. Sign in with your existing password.");
          setTimeout(() => navigate("/login", { replace: true }), 1400);
        } catch {
          toast.error(`ERROR · ${msg}`);
        }
      } else {
        toast.error(`ERROR · ${msg}`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-[900px] mx-auto px-6 lg:px-10 py-24" data-testid="terminal-success">
        <div className="te-terminal-window">
          <div className="border-b border-[#1F2833] px-5 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full bg-[#FF3355]/80"></span>
              <span className="h-2.5 w-2.5 rounded-full bg-[#1F2833] border border-[#B91C2C]/40"></span>
              <span className="h-2.5 w-2.5 rounded-full bg-[#1F2833] border border-[#B91C2C]/40"></span>
            </div>
            <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#C5C6C7]/70">
              traceelements --execution-complete
            </div>
            <div className="font-mono text-[11px] text-[#FF3355]">SUCCESS</div>
          </div>
          <div className="p-10 lg:p-14 text-center">
            <div className="inline-flex items-center justify-center h-16 w-16 border-2 border-[#FF3355] mb-8">
              <Check size={28} className="text-emerald-400" />
            </div>
            <h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight">
              Signal received.
            </h2>
            <p className="mt-4 text-[#C5C6C7] max-w-lg mx-auto">
              Your account is provisioned and your project brief is in our pipeline. Redirecting you
              to the client portal now — a co-founder will follow up within 24 hours.
            </p>
            <div className="mt-10 font-mono text-xs text-[#FF3355]">
              &gt; awaiting first.contact<span className="te-caret">_</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#0B0C10]">
      <section className="py-16 lg:py-20" data-testid="terminal-page">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
          <div className="mb-10">
            <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C] mb-6">
              // LAUNCH.TERMINAL
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[0.95]">
              Initialize <span className="text-[#FF3355]">your project.</span>
            </h1>
            <p className="mt-6 text-[#C5C6C7] max-w-2xl">
              The terminal below maps directly to our intake pipeline. Provisions your client
              portal account instantly — track project milestones, live previews, and payments in
              one place. No marketing forms. No gatekeepers. The co-founders read every signal.
            </p>
            <p className="mt-3 font-mono text-xs text-[#C5C6C7]/70">
              Already have an account?{" "}
              <Link to="/login" className="text-[#FF3355] hover:underline" data-testid="terminal-to-login">
                &gt; sign in here
              </Link>
            </p>
          </div>

          <form onSubmit={submit} className="te-terminal-window" data-testid="terminal-form">
            {/* Window header */}
            <div className="border-b border-[#1F2833] px-5 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full bg-[#FF3355]/80"></span>
                <span className="h-2.5 w-2.5 rounded-full bg-[#1F2833] border border-[#B91C2C]/40"></span>
                <span className="h-2.5 w-2.5 rounded-full bg-[#1F2833] border border-[#B91C2C]/40"></span>
              </div>
              <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#C5C6C7]/70">
                ~/traceelements/intake.sh
              </div>
              <div className="font-mono text-[11px] text-[#FF3355]">READY</div>
            </div>

            {/* Prompt */}
            <div className="px-6 lg:px-10 pt-8 font-mono text-sm text-[#C5C6C7]">
              <div><span className="text-[#FF3355]">user@traceelements</span> $ <span className="text-[#F5F5F7]">traceelements --initialize-project</span></div>
              <div className="mt-2 text-[#C5C6C7]/70">// initiating intake pipeline...</div>
            </div>

            {/* Step 1 — tracks */}
            <div className="px-6 lg:px-10 mt-10">
              <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#B91C2C] mb-4">
                &gt; Step 01 · Select your track (multi-select)
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {tracks.map((t) => {
                  const isOn = selectedTracks.includes(t.id);
                  return (
                    <button
                      type="button"
                      key={t.id}
                      onClick={() => toggleTrack(t.id)}
                      data-testid={`terminal-track-${t.id}`}
                      className={`text-left flex items-center gap-3 px-4 py-4 font-mono text-sm border transition-all ${
                        isOn
                          ? "border-[#FF3355] text-[#FF3355] bg-[#B91C2C]/10"
                          : "border-[#1F2833] text-[#C5C6C7] hover:border-[#B91C2C]"
                      }`}
                    >
                      <span className="text-[#FF3355]">[{isOn ? "x" : " "}]</span>
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Step 2 — timeline */}
            <div className="px-6 lg:px-10 mt-10">
              <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#B91C2C] mb-4">
                &gt; Step 02 · Define your timeline
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {timelines.map((t) => {
                  const isOn = timeline === t.id;
                  return (
                    <button
                      type="button"
                      key={t.id}
                      onClick={() => setTimeline(t.id)}
                      data-testid={`terminal-timeline-${t.id}`}
                      className={`text-left flex items-start gap-3 px-4 py-4 font-mono text-sm border transition-all ${
                        isOn
                          ? "border-[#FF3355] text-[#FF3355] bg-[#B91C2C]/10"
                          : "border-[#1F2833] text-[#C5C6C7] hover:border-[#B91C2C]"
                      }`}
                    >
                      <span className="text-[#FF3355]">({isOn ? "•" : " "})</span>
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Step 3 — identity */}
            <div className="px-6 lg:px-10 mt-10">
              <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#B91C2C] mb-4">
                &gt; Step 03 · Identity &amp; Access
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FieldInput
                  testid="terminal-input-name"
                  label="name"
                  value={name}
                  onChange={setName}
                  placeholder="e.g. Jordan Sato"
                />
                <FieldInput
                  testid="terminal-input-company"
                  label="company (optional)"
                  value={company}
                  onChange={setCompany}
                  placeholder="e.g. North Atlas Capital"
                />
                <FieldInput
                  testid="terminal-input-email"
                  label="direct email"
                  type="email"
                  value={email}
                  onChange={setEmail}
                  placeholder="jordan@northatlas.io"
                />
                <FieldInput
                  testid="terminal-input-password"
                  label="portal password · min 8 chars"
                  type="password"
                  value={password}
                  onChange={setPassword}
                  placeholder="••••••••"
                />
              </div>

              <div className="mt-6">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C5C6C7] mb-2">
                  &gt; project brief
                </div>
                <textarea
                  data-testid="terminal-input-brief"
                  value={brief}
                  onChange={(e) => setBrief(e.target.value)}
                  rows={6}
                  placeholder={"// what are we building?\n// describe the outcome you need…"}
                  className="w-full bg-[#0B0C10] border border-[#1F2833] focus:border-[#FF3355] outline-none p-4 font-mono text-sm text-[#F5F5F7] placeholder:text-[#C5C6C7]/40 resize-y"
                />
              </div>
            </div>

            {/* Submit */}
            <div className="px-6 lg:px-10 py-10 mt-6 border-t border-[#1F2833] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
              <div className="font-mono text-xs text-[#C5C6C7]">
                <span className="text-[#FF3355]">&gt;</span> ready to compile signal
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="terminal-submit"
                className="inline-flex items-center gap-2 bg-[#B91C2C] text-[#0B0C10] px-8 py-4 font-mono text-xs uppercase tracking-[0.22em] font-bold hover:bg-[#FF3355] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 size={14} className="animate-spin" /> EXECUTING...
                  </>
                ) : (
                  <>[ EXECUTE INITIALIZATION ]</>
                )}
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}

function FieldInput({ label, value, onChange, placeholder, type = "text", testid, disabled }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C5C6C7] mb-2">
        &gt; {label}
      </div>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full bg-[#0B0C10] border border-[#1F2833] focus:border-[#FF3355] outline-none px-4 py-3 font-mono text-sm text-[#F5F5F7] placeholder:text-[#C5C6C7]/40 disabled:text-[#FF3355] disabled:cursor-not-allowed"
      />
    </div>
  );
}
